// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/hooks/usePurchases.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, onSnapshot, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

export function usePurchases(userId) {
    const [purchases, setPurchases] = useState([]);

    const getValidDate = (val, fallback = new Date()) => {
        if (!val) return fallback;
        const d = val instanceof Date ? val : new Date(val);
        return isNaN(d.getTime()) ? fallback : d;
    };

    useEffect(() => {
        if (!userId) {
            setPurchases([]);
            return;
        }

        const collectionRef = collection(db, 'users', userId, 'purchases');
        const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
            const purchasesData = snapshot.docs.map(doc => {
                const data = doc.data();
                const purchaseDateTimestamp = data.purchaseDate;
                let finalPurchaseDate = null;
                if (purchaseDateTimestamp) {
                    finalPurchaseDate = purchaseDateTimestamp.toDate ? purchaseDateTimestamp.toDate() : new Date(purchaseDateTimestamp);
                }

                return {
                    ...data,
                    id: doc.id,
                    purchaseDate: finalPurchaseDate,
                    installments: data.installments?.map(inst => ({
                        ...inst, 
                        dueDate: inst.dueDate?.toDate ? inst.dueDate.toDate() : new Date(inst.dueDate)
                    })) || [],
                    payments: data.payments?.map(pay => ({
                        ...pay,
                        date: pay.date?.toDate ? pay.date.toDate() : new Date(pay.date)
                    })) || []
                };
            });

            purchasesData.sort((a, b) => b.purchaseDate - a.purchaseDate);
            setPurchases(purchasesData);
        });

        return () => unsubscribe();
    }, [userId]);

    const purchaseFlow = useMemo(() => {
        const initial = { payables: [], paidHistory: [], byYear: {}, availableYears: [], summary: { toPayThisMonth: 0, paidThisMonth: 0 } };
        if (!purchases.length) return initial;

        const financialDataByYear = {};

        const ensureYear = (year) => {
            const yearStr = String(year);
            if (!financialDataByYear[yearStr]) {
                financialDataByYear[yearStr] = { monthlyToPay: Array(12).fill(0), monthlyPaid: Array(12).fill(0) };
            }
        };

        const safeDate = (val) => {
            if (!val) return null;
            if (val instanceof Date) return isNaN(val) ? null : val;
            const parsed = new Date(val);
            return isNaN(parsed) ? null : parsed;
        };

        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const startOfToday = new Date(currentYear, currentMonth, now.getDate());

        let totalToPayThisMonth = 0;
        let totalPaidThisMonth = 0;

        const paidHistory = purchases.flatMap(purchase => 
            (purchase.payments || []).map(payment => ({
                ...payment,
                purchaseId: purchase.id,
                description: purchase.description,
            }))
        ).filter(p => safeDate(p.date) !== null).sort((a, b) => b.date - a.date);

        paidHistory.forEach(item => {
            const d = safeDate(item.date);
            if(d) {
                const year = d.getFullYear();
                const month = d.getMonth();
                
                ensureYear(year);
                financialDataByYear[String(year)].monthlyPaid[month] += Number(item.amount || 0);

                if (year === currentYear && month === currentMonth) {
                    totalPaidThisMonth += Number(item.amount || 0);
                }
            }
        });

        const payables = purchases.flatMap(purchase => 
            (purchase.installments || []).filter(inst => inst.status === 'Pendente').map(inst => ({
                ...inst,
                purchaseId: purchase.id,
                description: purchase.description,
                amountToPay: Number(inst.amount - (inst.paidAmount || 0)),
            }))
        ).sort((a, b) => a.dueDate - b.dueDate);

        payables.forEach(item => {
            const d = safeDate(item.dueDate);
            if(d) {
                const year = d.getFullYear();
                const month = d.getMonth();
                
                ensureYear(year);
                financialDataByYear[String(year)].monthlyToPay[month] += item.amountToPay;

                if ((year === currentYear && month === currentMonth) || d < startOfToday) {
                    totalToPayThisMonth += item.amountToPay;
                }
            }
        });

        return {
            payables, 
            paidHistory, 
            byYear: financialDataByYear,
            availableYears: Object.keys(financialDataByYear).sort((a, b) => b - a),
            summary: { 
                toPayThisMonth: totalToPayThisMonth, 
                paidThisMonth: totalPaidThisMonth 
            }
        };
    }, [purchases]);

    const addPurchase = useCallback(async (purchaseData) => {
        if (!userId) return;
        const newPurchaseRef = doc(collection(db, 'users', userId, 'purchases'));
        
        const safePurchaseDate = getValidDate(purchaseData.purchaseDate);

        let processedInstallments = (purchaseData.installments || []).map(inst => ({
            ...inst,
            dueDate: getValidDate(inst.dueDate)
        }));

        let processedPayments = (purchaseData.payments || []).map(pay => ({
            ...pay,
            date: getValidDate(pay.date)
        }));

        let status = purchaseData.status || 'Pendente';

        if (purchaseData.paymentType === 'À Vista') {
            status = 'Concluída';
            if (processedInstallments.length > 0) {
                processedInstallments[0].status = 'Paga';
                processedInstallments[0].paidAmount = processedInstallments[0].amount;
            } else {
                processedInstallments = [{ 
                    installmentNumber: 1, 
                    amount: purchaseData.totalAmount, 
                    dueDate: safePurchaseDate, 
                    status: 'Paga', 
                    paidAmount: purchaseData.totalAmount 
                }];
            }
            if (processedPayments.length === 0) {
                processedPayments = [{ 
                    id: crypto.randomUUID(), 
                    amount: purchaseData.totalAmount, 
                    date: processedInstallments[0].dueDate, 
                    method: purchaseData.paymentMethod || 'Pix' 
                }];
            }
        }

        await setDoc(newPurchaseRef, { 
            ...purchaseData, 
            purchaseDate: safePurchaseDate, 
            status, 
            installments: processedInstallments, 
            payments: processedPayments, 
            createdAt: serverTimestamp() 
        });
    }, [userId]);

    const updatePurchase = useCallback(async (purchaseId, updatedData) => {
        if (!userId) return;
        await updateDoc(doc(db, 'users', userId, 'purchases', purchaseId), updatedData);
    }, [userId]);

    const deletePurchase = useCallback(async (purchaseId) => {
        if (!userId) return;
        await deleteDoc(doc(db, 'users', userId, 'purchases', purchaseId));
    }, [userId]);

    const deleteMultiplePurchases = useCallback(async (purchaseIds) => {
        if (!userId || !purchaseIds || purchaseIds.length === 0) return;
        const batch = writeBatch(db);
        purchaseIds.forEach(id => {
            const ref = doc(db, 'users', userId, 'purchases', id);
            batch.delete(ref);
        });
        await batch.commit();
    }, [userId]);

    const addPaymentToPurchase = useCallback(async (purchaseId, paymentAmount, paymentMethod, dateOfPayment) => {
        if (!userId) return;
        const purchaseRef = doc(db, 'users', userId, 'purchases', purchaseId);
        const purchase = purchases.find(p => p.id === purchaseId);
        if (!purchase) throw new Error("Compra não encontrada");
        
        let paymentLeftToApply = paymentAmount;
        const updatedInstallments = JSON.parse(JSON.stringify(purchase.installments));
        
        for (const inst of updatedInstallments) {
            if (inst.status === 'Pendente' && paymentLeftToApply > 0) {
                const alreadyPaid = inst.paidAmount || 0;
                const amountOwed = inst.amount - alreadyPaid;
                
                if (paymentLeftToApply >= amountOwed) {
                    paymentLeftToApply -= amountOwed;
                    inst.paidAmount = inst.amount;
                    inst.status = 'Paga';
                } else {
                    inst.paidAmount = alreadyPaid + paymentLeftToApply;
                    paymentLeftToApply = 0;
                }
            }
        }
        
        const safePaymentDate = getValidDate(dateOfPayment);

        const newPaymentRecord = { id: crypto.randomUUID(), amount: paymentAmount, date: safePaymentDate, method: paymentMethod };
        const updatedPayments = [...(purchase.payments || []), newPaymentRecord];
        const allPaid = updatedInstallments.every(inst => inst.status === 'Paga');
        
        await updateDoc(purchaseRef, { installments: updatedInstallments, payments: updatedPayments, status: allPaid ? 'Concluída' : 'Pendente' });
    }, [purchases, userId]);

    // CORREÇÃO DA BAIXA EM LOTE COM PAGAMENTO PARCIAL (CASCATA)
    const bulkAddPaymentsToPurchases = useCallback(async (itemsToPay, paymentDate, paymentMethod) => {
        if (!userId || !itemsToPay || itemsToPay.length === 0) return;
        const batch = writeBatch(db);
        const grouped = {};
        itemsToPay.forEach(item => {
            if (!grouped[item.purchaseId]) grouped[item.purchaseId] = [];
            grouped[item.purchaseId].push(item);
        });

        const safePaymentDate = getValidDate(paymentDate);

        for (const purchaseId in grouped) {
            const purchaseRef = doc(db, 'users', userId, 'purchases', purchaseId);
            const purchase = purchases.find(p => p.id === purchaseId);
            if (!purchase) continue;

            // Calcula o quanto o usuário está pagando para ESTA compra específica neste lote
            let totalPaidInThisBatch = 0;
            grouped[purchaseId].forEach(targetInst => {
                totalPaidInThisBatch += Number(targetInst.amountToPay);
            });

            if (totalPaidInThisBatch > 0) {
                const newPaymentRecord = { id: crypto.randomUUID(), amount: totalPaidInThisBatch, date: safePaymentDate, method: paymentMethod };
                
                // Junta o novo pagamento ao histórico de pagamentos dessa compra
                const updatedPayments = [...(purchase.payments || []), newPaymentRecord];
                
                // Recalcula todas as parcelas do zero no modo Cascata
                const updatedInstallments = purchase.installments.map(inst => ({ ...inst, paidAmount: 0, status: 'Pendente' }));
                let totalPaidLeft = updatedPayments.reduce((sum, p) => sum + Number(p.amount), 0);
                
                for (const inst of updatedInstallments) {
                    if (totalPaidLeft > 0) {
                        if (totalPaidLeft >= inst.amount) {
                            inst.paidAmount = inst.amount;
                            inst.status = 'Paga';
                            totalPaidLeft -= inst.amount;
                        } else {
                            inst.paidAmount = totalPaidLeft;
                            totalPaidLeft = 0;
                        }
                    }
                }

                const allPaid = updatedInstallments.every(i => i.status === 'Paga');

                batch.update(purchaseRef, {
                    installments: updatedInstallments,
                    payments: updatedPayments,
                    status: allPaid ? 'Concluída' : 'Pendente'
                });
            }
        }
        await batch.commit();
    }, [purchases, userId]);

    const deleteExpensePayment = useCallback(async (purchaseId, paymentId) => {
        if (!userId) return;
        const purchaseRef = doc(db, 'users', userId, 'purchases', purchaseId);
        const purchase = purchases.find(p => p.id === purchaseId);
        if (!purchase) return;

        const updatedPayments = (purchase.payments || []).filter(p => p.id !== paymentId);
        const updatedInstallments = purchase.installments.map(inst => ({ ...inst, paidAmount: 0, status: 'Pendente' }));

        let totalPaidLeft = updatedPayments.reduce((sum, p) => sum + Number(p.amount), 0);
        for (const inst of updatedInstallments) {
            if (totalPaidLeft > 0) {
                if (totalPaidLeft >= inst.amount) {
                    inst.paidAmount = inst.amount;
                    inst.status = 'Paga';
                    totalPaidLeft -= inst.amount;
                } else {
                    inst.paidAmount = totalPaidLeft;
                    totalPaidLeft = 0;
                }
            }
        }
        const allPaid = updatedInstallments.every(i => i.status === 'Paga');
        await updateDoc(purchaseRef, { payments: updatedPayments, installments: updatedInstallments, status: allPaid ? 'Concluída' : 'Pendente' });
    }, [purchases, userId]);

    const deleteMultipleExpensePayments = useCallback(async (paymentIds) => {
        if (!userId || !paymentIds || paymentIds.length === 0) return;
        const batch = writeBatch(db);
        const paymentsToDeleteByPurchase = {};
        
        purchases.forEach(purchase => {
            const matches = (purchase.payments || []).filter(p => paymentIds.includes(p.id));
            if (matches.length > 0) paymentsToDeleteByPurchase[purchase.id] = matches.map(p => p.id);
        });

        for (const purchaseId in paymentsToDeleteByPurchase) {
            const purchase = purchases.find(p => p.id === purchaseId);
            const idsToRemove = paymentsToDeleteByPurchase[purchaseId];
            const updatedPayments = (purchase.payments || []).filter(p => !idsToRemove.includes(p.id));
            const updatedInstallments = purchase.installments.map(inst => ({ ...inst, paidAmount: 0, status: 'Pendente' }));

            let totalPaidLeft = updatedPayments.reduce((sum, p) => sum + Number(p.amount), 0);
            for (const inst of updatedInstallments) {
                if (totalPaidLeft > 0) {
                    if (totalPaidLeft >= inst.amount) {
                        inst.paidAmount = inst.amount;
                        inst.status = 'Paga';
                        totalPaidLeft -= inst.amount;
                    } else {
                        inst.paidAmount = totalPaidLeft;
                        totalPaidLeft = 0;
                    }
                }
            }
            const allPaid = updatedInstallments.every(i => i.status === 'Paga');
            const purchaseRef = doc(db, 'users', userId, 'purchases', purchaseId);
            batch.update(purchaseRef, { payments: updatedPayments, installments: updatedInstallments, status: allPaid ? 'Concluída' : 'Pendente' });
        }
        await batch.commit();
    }, [purchases, userId]);

    const updateExpensePayment = useCallback(async (purchaseId, paymentId, updatedPaymentData) => {
        if (!userId) return;
        const purchaseRef = doc(db, 'users', userId, 'purchases', purchaseId);
        const purchase = purchases.find(p => p.id === purchaseId);
        if (!purchase) return;

        const updatedPayments = (purchase.payments || []).map(p => 
            p.id === paymentId ? { ...p, ...updatedPaymentData } : p
        );

        const updatedInstallments = purchase.installments.map(inst => ({ ...inst, paidAmount: 0, status: 'Pendente' }));

        let totalPaidLeft = updatedPayments.reduce((sum, p) => sum + Number(p.amount), 0);
        for (const inst of updatedInstallments) {
            if (totalPaidLeft > 0) {
                if (totalPaidLeft >= inst.amount) {
                    inst.paidAmount = inst.amount;
                    inst.status = 'Paga';
                    totalPaidLeft -= inst.amount;
                } else {
                    inst.paidAmount = totalPaidLeft;
                    totalPaidLeft = 0;
                }
            }
        }
        const allPaid = updatedInstallments.every(i => i.status === 'Paga');
        await updateDoc(purchaseRef, { payments: updatedPayments, installments: updatedInstallments, status: allPaid ? 'Concluída' : 'Pendente' });
    }, [purchases, userId]);

    return { 
        purchases, purchaseFlow, addPurchase, updatePurchase, deletePurchase, deleteMultiplePurchases,
        addPaymentToPurchase, bulkAddPaymentsToPurchases, deleteExpensePayment, deleteMultipleExpensePayments, updateExpensePayment
    };
}